import axios, { AxiosResponse } from 'axios';
import type { AxiosRequestConfig } from 'axios';
import { DocumentNode } from 'graphql';

export const baseUrl = ''; // 'http://localhost:5183';

const config = {
    baseURL: `${baseUrl}`,
    headers: {
        'Content-Type': 'application/json'
    },
    validateStatus: () => true,
};

// #region graph
export interface GraphResponse<T> {
    data?: T,
    status: number,
    statusTest: string,
    errors?: Array<{
        message: string,
        locations: Array<{
            line: number,
            column: number
        }>,
        path: string[],
        extensions: {
            type: string,
            field: string,
            responseName: string,
            specifiedBy: string
        }
    }>
    success: boolean
}

const graphRequest = axios.create(config);

export const graph = async <TOut = any, TIn = any>(data: { query: string | DocumentNode, variables?: TIn }, config?: AxiosRequestConfig): Promise<GraphResponse<TOut>> => {
    if (typeof data.query != 'string') data.query = (data.query.loc?.source?.body || data.query) + '';

    var response = await graphRequest.post<GraphResponse<TOut>>('/graphql', data, config);

    // if(response.data.errors) throw response.data.errors;

    return {
        ...response.data,
        status: response.status,
        statusTest: response.statusText,
        success: response.status < 300 && response.status > 199 ? true : false
    };
};
// #endregion graph

// #region api

const apicofig = axios.create({
    ...config,
    baseURL: `${baseUrl}/api`,
});

const getParams = (params: Record<string, string | number | boolean | undefined | null>) => {
    const listParams = [];
    for (const key in params) {
        if (Object.prototype.hasOwnProperty.call(params, key)) {
            listParams.push(`${key}=${encodeURIComponent(params[key] ?? '')}`);
        }
    }

    return listParams.join('&');
};

export interface SunriseApiResponse<T, TR> extends AxiosResponse<T, TR> {
    success: boolean
}

export interface IRequest {
    url: string,
    params: Record<string, string | number | boolean | undefined | null>,
    config?: AxiosRequestConfig
}

export const api = {
    get: async <T, TR = any>(config: IRequest | string) => {
        let response;
        if (typeof config === 'string') {
            response = await apicofig.get<T>(config);
        }
        else {
            const url = config.url + "?" + getParams(config.params);
            response = await apicofig.get<T>(url, config.config);
        }

        return {
            ...response,
            success: response.status > 199 && response.status < 300
        } as SunriseApiResponse<T, TR>;
    },

    delete: async <T, TR = any>(config: IRequest | string) => {
        let response;
        if (typeof config === 'string') {
            response = await apicofig.delete<T>(config);
        }
        else {
            const url = config.url + "?" + getParams(config.params);
            response = await apicofig.delete<T>(url, config.config);
        }

        return {
            ...response,
            success: response.status > 199 && response.status < 300
        } as SunriseApiResponse<T, TR>;
    },

    post: async <T = any, TR = any>(config: IRequest | string, model?: any) => {
        let response;
        if (typeof config === 'string') {
            response = await apicofig.post<T>(config, model);
        }
        else {
            const url = config.url + "?" + getParams(config.params);
            response = await apicofig.post<T>(url, model, config.config);
        }

        return {
            ...response,
            success: response.status > 199 && response.status < 300
        } as SunriseApiResponse<T, TR>;
    },
    put: async <T = any, TR = any>(config: IRequest | string, model?: any) => {
        let response;
        if (typeof config === 'string') {
            response = await apicofig.put<T>(config, model);
        }
        else {
            const url = config.url + "?" + getParams(config.params);
            response = await apicofig.put<T>(url, model, config.config);
        }

        return {
            ...response,
            success: response.status > 199 && response.status < 300
        } as SunriseApiResponse<T, TR>;
    },
    patch: async <T = any, TR = any>(config: IRequest | string, model?: any) => {
        let response;
        if (typeof config === 'string') {
            response = await apicofig.patch<T>(config, model);
        }
        else {
            const url = config.url + "?" + getParams(config.params);
            response = await apicofig.patch<T>(url, model, config.config);
        }

        return {
            ...response,
            success: response.status > 199 && response.status < 300
        } as SunriseApiResponse<T, TR>;
    },
    postFile: async <T = any, TR = any>(config: IRequest | string, file: File, requestConfig?: AxiosRequestConfig) => {
        const formData = new FormData();
        formData.append("file", file);
        const url = typeof config == 'string'
            ? config
            : (config.url + "?" + getParams(config.params));

        const axiosInstance = axios.create({
            baseURL: `${baseUrl}/api`,
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            validateStatus: () => true,
        });

        const response = await axiosInstance.post<T>(url, formData, requestConfig);

        return {
            ...response,
            success: response.status > 199 && response.status < 300
        } as SunriseApiResponse<T, TR>;
    }
};
// #endregion api
